import type { BillingInterval, BillingStatus } from './api/types';

/**
 * The plans as quizee.io publishes them, in the words the pricing page uses.
 *
 * The same table exists on the server in `packages/db/src/plans.ts`, which is the one that decides
 * anything: what a publisher is charged and what their allowance is are settled there, and nothing
 * a browser sends names a price. This copy is presentation only, so the screen can say what a plan
 * costs before anybody presses a button, and it has to keep saying what the pricing page says.
 * When the server's table moves to `packages/core`, as the comment on it intends, this one should
 * be deleted and the shared one imported.
 *
 * The annual figures are the monthly ones times ten, which is the promise on the pricing page:
 * annual billing takes two months off. Change one of these and the page and the product disagree
 * about money, which is the kind of drift a customer finds before we do.
 */
export interface PlanOffer {
  readonly plan: string;
  /** What the pricing page calls it, and therefore what this screen calls it. */
  readonly label: string;
  readonly monthly: string | null;
  readonly annual: string | null;
  readonly brands: string;
  readonly responses: string;
  /** True for a plan somebody can buy here. Free is where you start, Network is a conversation. */
  readonly boughtHere: boolean;
}

export const PLAN_OFFERS: readonly PlanOffer[] = [
  {
    plan: 'free',
    label: 'Free',
    monthly: '£0 a month',
    annual: null,
    brands: '1 brand',
    responses: '5,000 responses a month',
    boughtHere: false,
  },
  {
    plan: 'single',
    label: 'Single title',
    monthly: '£250 a month',
    annual: '£2,500 a year',
    brands: '1 brand',
    responses: '50,000 responses a month',
    boughtHere: true,
  },
  {
    plan: 'group',
    label: 'Group',
    monthly: '£900 a month',
    annual: '£9,000 a year',
    brands: 'Up to 8 brands',
    responses: '500,000 responses a month',
    boughtHere: true,
  },
  {
    plan: 'network',
    label: 'Network',
    monthly: null,
    annual: null,
    brands: 'Unlimited brands',
    responses: 'Agreed with you',
    boughtHere: false,
  },
];

/** The plans a checkout can be started for, in the order they are offered. */
export const BUYABLE_PLANS: readonly PlanOffer[] = PLAN_OFFERS.filter((offer) => offer.boughtHere);

export function offerFor(plan: string): PlanOffer | undefined {
  return PLAN_OFFERS.find((offer) => offer.plan === plan);
}

/** What a plan costs on one interval, or null where the number is not a published one. */
export function priceWords(offer: PlanOffer, interval: BillingInterval): string | null {
  return interval === 'annual' ? offer.annual : offer.monthly;
}

/**
 * The saving, in the pricing page's own sentence.
 *
 * Copied rather than reworded on purpose. A publisher who read the page and then met a second way
 * of saying the same thing would have to work out whether the two offers were the same offer.
 */
export const ANNUAL_SAVING =
  'Annual billing takes two months off: a year costs what ten months would.';

/** Where a plan that is not sold on a page gets settled. */
export const CONTACT_EMAIL = 'hello@quizee.io';

const INTERVAL_WORDS: Readonly<Record<BillingInterval, string>> = {
  monthly: 'Monthly',
  annual: 'Annual',
};

export function intervalWords(interval: BillingInterval): string {
  return INTERVAL_WORDS[interval];
}

/**
 * Stripe's statuses in a publisher's words, and in UK English.
 *
 * The failures are written as what happened rather than as a label, because "unpaid" beside a
 * date is a sentence somebody has to decode while worried. Nothing here says anything has been
 * switched off, because nothing has been: a card that failed does not take a publisher's
 * interactions off their site, and the plan record is deliberately separate from this so that it
 * cannot.
 */
const STATUS_WORDS: Readonly<Record<BillingStatus, string>> = {
  none: 'No subscription',
  trialing: 'On trial',
  active: 'Active',
  past_due: 'A payment did not go through',
  cancelled: 'Cancelled',
};

export function statusWords(status: BillingStatus): string {
  // Indexed rather than switched so an unrecognised status from a newer API reads as itself
  // instead of throwing a screen away over a word.
  return STATUS_WORDS[status] ?? status;
}

/**
 * The statuses that mean somebody is a paying customer right now.
 *
 * What it is for: a checkout must not be offered beside a live subscription, because that is how
 * somebody ends up paying twice. A cancelled or abandoned one is not in here, so a publisher who
 * stopped can start again without asking us.
 */
const PAYING: readonly BillingStatus[] = ['trialing', 'active', 'past_due'];

export function isPaying(status: BillingStatus): boolean {
  return PAYING.includes(status);
}

/**
 * What the period end means, which depends on where the subscription has got to.
 *
 * A date on its own is ambiguous: the same day is a renewal on an active subscription and the last
 * day of access on a cancelled one, and those are not the same news.
 */
export function periodWords(status: BillingStatus, day: string): string {
  if (status === 'cancelled') return `This runs until ${day} and is not renewed after that.`;
  if (status === 'trialing') return `Your trial runs until ${day}.`;
  if (status === 'past_due') {
    return `The period paid for runs to ${day}. Stripe will try the card again, and nothing you have published changes in the meantime.`;
  }
  return `This renews on ${day}.`;
}

const MONTH_NAME = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long' });

/**
 * "September" from a `YYYY-MM`, for saying which month a grant belongs to.
 *
 * The 15th, so no offset in either direction can land the label on a different month. Anything
 * that is not a month comes back as null, and the screen then says "this month" instead of
 * printing a key at a publisher.
 */
export function monthName(month: string | null | undefined): string | null {
  if (typeof month !== 'string') return null;
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
  if (match === null) return null;
  return MONTH_NAME.format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 15)));
}

/** Thousands separated the way a British newsroom writes them. */
export function count(value: number): string {
  return value.toLocaleString('en-GB');
}
